import { useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, faqPageSchema, serviceSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowUpRight, ShieldCheck, Globe2, MapPin, Languages, Zap, Sparkles, HeartHandshake,
  Plane, Briefcase, Home as HomeIcon, Users, Building2, KeyRound, Rocket
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg";
const PREMIUM_IMG = "https://images.pexels.com/photos/14505912/pexels-photo-14505912.jpeg";
const BASIC_IMG = "https://images.unsplash.com/photo-1772338537689-056082f100a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxtYW4lMjBhc3NlbWJsaW5nJTIwZnVybml0dXJlJTIwaG9tZXxlbnwwfHx8fDE3Nzc4MjA2NDZ8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNvdXBsZSUyMHVucGFja2luZyUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3Nzc4MjA2MzR8MA&ixlib=rb-4.1.0&q=85";
const MUNICH_IMG = "https://images.unsplash.com/photo-1652694664151-0dbc97712aaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtdW5pY2glMjBzdHJlZXQlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc3ODIwNjM0fDA&ixlib=rb-4.1.0&q=85";
const CONCIERGE_IMG = "https://images.unsplash.com/photo-1758518727888-ffa196002e59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb25jaWVyZ2UlMjB3ZWxjb21pbmd8ZW58MHx8fHwxNzc3ODIwNjQ2fDA&ixlib=rb-4.1.0&q=85";

const useCases = [
  "Sie ziehen nach München",
  "Sie ziehen innerhalb Münchens um",
  "Sie richten Ihre Wohnung nach Mietvertragsunterzeichnung ein",
  "Sie wechseln von temporärer zu dauerhafter Unterkunft",
  "Sie brauchen Ihr Homeoffice einsatzbereit vor dem ersten Arbeitstag",
  "Sie brauchen Ihr Zuhause fertig, bevor Ihre Familie ankommt",
];

const problems = [
  "Sprachbarrieren mit Händlern und Lieferunternehmen",
  "Leere Wohnung ohne Möbel und ohne Küche",
  "Lieferungen koordinieren während Anmeldung, Bank und Bürokratie laufen",
  "Möbelmontage nach langem internationalen Umzug",
  "Verpackungen und Recyclingvorschriften richtig handhaben",
  "Funktionierendes Homeoffice schnell einrichten",
  "Wohnung nach dem Einzug anpassen (neue Möbel, Ersatz, Upgrades)",
];

const moveInItems = [
  "Möbelplanung & Produktliste",
  "Bestellkoordination",
  "Lieferplanung",
  "Möbelmontage / Möbelaufbau",
  "Zimmer-Einrichtung (Schlafzimmer, Wohnzimmer, Arbeitsbereich)",
  "Verpackungsentsorgung",
];
const beyondItems = [
  "Homeoffice-Einrichtung vor dem ersten Arbeitstag",
  "Zusätzliche Möbelmontage und Upgrades",
  "Retouren und Ersatzlieferungen koordinieren",
  "Wohnung neu konfigurieren (neue Bedürfnisse, Familienzuwachs)",
  "Auszugsvorbereitung und Unterstützung",
];

const steps = [
  { n: "01", title: "Discovery Call", desc: "Wir verstehen Ihre Wohnung, Ihren Zeitplan, Prioritäten und aktuelle Situation (Einzug, Upgrade oder Wechsel)." },
  { n: "02", title: "Planung & Koordination", desc: "Sie teilen Vorlieben oder Produktliste. Wir beschaffen Möbel von vertrauten Anbietern — IKEA, Home24, XXXLutz." },
  { n: "03", title: "Lieferung & Aufbau", desc: "Wir koordinieren Lieferungen, montieren die Möbel und richten Ihren Raum ein." },
  { n: "04", title: "Laufende Unterstützung", desc: "Optional — wir bleiben verfügbar für Anpassungen, Ergänzungen und zukünftige Bedürfnisse." },
  { n: "05", title: "In ein fertiges Zuhause einziehen", desc: "Kommen Sie an — oder leben Sie weiter — ohne Unterbrechung." },
];

const audiences = [
  { icon: Plane, label: "Expats, die nach München ziehen" },
  { icon: Briefcase, label: "Professionals, die eine neue Rolle starten" },
  { icon: HomeIcon, label: "Remote-Arbeiter mit Bedarf an funktionalem Setup" },
  { icon: Users, label: "Familien, die ankommen oder wachsen" },
  { icon: KeyRound, label: "Mieter im Wechsel von Temp- zu Langzeit-Wohnung" },
  { icon: Building2, label: "Corporate HR / Relocation-Partner" },
  { icon: Sparkles, label: "Möblierte Apartment-Hosts und Vermieter" },
];

const moreSituations = [
  "Umzug aus temporärer Unterkunft (Airbnb, Serviced Apartment) in dauerhaftes Zuhause",
  "Unmöblierte Wohnung von Grund auf einrichten",
  "Homeoffice vor dem ersten Arbeitstag vorbereiten",
  "Wohnung nach dem Einzug anpassen (neue Möbel, Ersatz, Upgrades)",
  "Familienankunft oder geänderte Wohnbedürfnisse unterstützen",
  "Wohnungen für Auszug oder Untervermietung vorbereiten",
  "Corporate-Wohnungsbestand standardisieren und betreuen",
];

const packages = [
  {
    code: "S",
    title: "IKEA Möbelmontage",
    audience: "Für IKEA-Liebhaber: PAX, MALM, BEKANT, BILLY u. v. m.",
    desc: "Dedizierter IKEA Aufbauservice München — von einem PAX-Schrank bis zum vollständigen IKEA-Zimmer.",
    includes: ["PAX, PLATSA, BRIMNES Kleiderschränke", "MALM, HEMNES, SONGESAND Betten", "BEKANT, MICKE, ALEX Schreibtische", "Lieferung & Annahme", "Verpackungsentsorgung"],
    price: "Ab €450",
    img: BASIC_IMG,
    tag: "Nur IKEA",
    link: "/ikea-moebelmontage-muenchen",
    learnMore: "Mehr zur IKEA Möbelmontage →",
  },
  {
    code: "M",
    title: "Komplette Wohnungseinrichtung",
    audience: "Ideal für Expats, Paare und 1–2-Zimmer-Wohnungen",
    desc: "Wir richten Ihr gesamtes Zuhause ein, damit Sie ohne Chaos einziehen können.",
    includes: ["Mehrzimmer-Planung", "Liefermanagement", "Komplette Montage & Einrichtung", "Organisation", "Verpackungsentsorgung", "Abschluss-Begehung"],
    price: "Ab €1.290",
    img: PREMIUM_IMG,
    tag: "Am meisten gebucht",
    link: null,
  },
  {
    code: "L",
    title: "White Glove Relocation Concierge",
    audience: "Für Familien, Führungskräfte und Premium-Relocations",
    desc: "Komplette done-for-you-Einrichtung mit Priorisierung und laufender Betreuung.",
    includes: ["Pre-Arrival-Planung", "Komplette Wohnungseinrichtung", "Priorisierte Termine", "Individuelle Koordination", "Premium-Support", "Post-Move-Unterstützung"],
    price: "Individuelles Angebot",
    img: CONCIERGE_IMG,
    tag: "Premium",
    link: null,
  },
];

const whyUs = [
  { icon: Languages, t: "Englischsprachige Kommunikation", d: "Spanisch ebenfalls verfügbar." },
  { icon: MapPin, t: "Münchner Lokal-Know-how", d: "Lokale Eigenheiten, lokale Lösungen." },
  { icon: Plane, t: "Für Expat-Bedürfnisse entwickelt", d: "Auf internationales Leben zugeschnitten." },
  { icon: Zap, t: "Schnelle Reaktion", d: "Wohnung in 48 Stunden fertig, wenn nötig." },
  { icon: Sparkles, t: "White-Glove-Erfahrung", d: "Von der ersten E-Mail bis zur letzten Schraube." },
  { icon: HeartHandshake, t: "Unterstützung über den Umzug hinaus", d: "Wir bleiben verfügbar, wenn sich Ihre Bedürfnisse ändern." },
];

const faqs = [
  { q: "Können Sie nach meinem Einzug helfen?", a: "Ja — viele Kunden nutzen unseren Service nach dem Umzug, um Setup zu verbessern, zu erweitern oder anzupassen." },
  { q: "Kaufen Sie Möbel für mich?", a: "Wir koordinieren basierend auf Ihrer freigegebenen Produktliste und Vorlieben." },
  { q: "Können Sie vor meiner Ankunft helfen?", a: "Ja — Planung und Koordination können remote beginnen, sofern Zugang zur Wohnung gewährleistet werden kann." },
  { q: "Arbeiten Sie nur mit IKEA?", a: "Nein — IKEA kann Teil der Lösung sein, aber wir fokussieren uns auf praktische Einrichtung. Wir arbeiten auch mit Home24, XXXLutz und anderen." },
  { q: "Installieren Sie Küchen, Elektrik oder Sanitäranlagen?", a: "Nein — zulassungspflichtige Fachgewerke sind ausgeschlossen." },
];

export default function LandingDE() {
  const { openDialog } = useBookCall();

  useSeo({
    title: "Möbelmontage München für Expats — IKEA, Wohnungseinrichtung & Move-In Concierge | MUConcierge",
    description:
      "MUConcierge: End-to-End Möbelmontage-Service für Expats in München. Wir montieren Ihre Möbel professionell und schnell, damit Sie stressfrei in Ihre neue Wohnung einziehen können.",
    path: "/de",
    lang: "de",
    jsonLd: [
      faqPageSchema(faqs),
      ...packages.map((p) =>
        serviceSchema({
          name: p.title,
          description: p.desc,
          url: typeof window !== "undefined" ? window.location.origin + (p.link || "/de/leistungen") : undefined,
        })
      ),
    ],
    alternates: { en: "/", de: "/de" },
  });

  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Moderne Wohnung in München" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="max-w-4xl fade-up">
            <p className="tag-line text-[#FAF9F6]/80 mb-6">MUConcierge · Münchner Move-In Concierge</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FAF9F6] tracking-tighter leading-[1.05]">
              Möbelmontage
              <span className="block italic font-light text-[#E8C9B6]">München.</span>
            </h1>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl text-[#FAF9F6]/95 italic font-light tracking-tight leading-tight max-w-3xl">
              MUConcierge: Exklusiver Rundum-Service für die Möbelmontage – für Expats in München.
            </h2>
            <p className="mt-8 text-lg text-[#FAF9F6]/85 max-w-2xl leading-relaxed font-light">
              Wir helfen Expats und internationalen Professionals dabei, ihr Münchner Zuhause einzurichten, anzupassen und zu betreuen — von der Ankunft bis zum Alltag. Möbelplanung, Lieferkoordination, Möbelmontage, Einrichtung und laufender Support — wir übernehmen das.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => openDialog({ source: "de-hero", service: "Discovery Call" })}
                className="rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] text-base px-8 py-6"
                data-testid="hero-discovery-btn"
              >
                Kostenloses Erstgespräch
                <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="lg"
                onClick={() => openDialog({ source: "de-hero-48h", service: "48h Express-Setup" })}
                variant="outline"
                className="rounded-sm bg-transparent border-[#FAF9F6]/40 text-[#FAF9F6] hover:bg-[#FAF9F6]/10 hover:text-[#FAF9F6] text-base px-8 py-6"
                data-testid="hero-48h-btn"
              >
                Wohnung in 48 Stunden bereit
              </Button>
            </div>
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#FAF9F6]/80">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Vollständig versichert</div>
              <div className="flex items-center gap-2"><Globe2 className="w-4 h-4" strokeWidth={1.5} /> Englisch- &amp; deutschsprachig</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} /> München</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-y border-[#E5E2DC] bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-7 text-center">
          <p className="tag-line text-[#1C3326]">
            Für Expats, Professionals, Familien und Corporate-Relocations in München.
          </p>
        </div>
      </section>

      {/* USE CASES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 md:py-24">
        <p className="tag-line text-[#C26D5C] mb-3 text-center">Anwendungsfälle</p>
        <p className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight text-center mb-12 max-w-3xl mx-auto leading-tight">
          Wann MUConcierge für Sie sinnvoll ist.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {useCases.map((u, i) => (
            <div key={i} className="bg-white border border-[#E5E2DC] p-5 flex items-start gap-3 transition-all duration-300 hover:border-[#1C3326]">
              <div className="w-1.5 h-1.5 bg-[#C26D5C] mt-2 shrink-0" />
              <p className="text-[#1A201C] text-sm leading-relaxed">{u}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <p className="tag-line text-[#C26D5C] mb-4">Das Problem</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
              Nach Deutschland ziehen ist aufregend.
              <span className="block italic font-light text-[#C26D5C]">Möbel montieren ist nicht.</span>
            </h2>
            <p className="mt-6 text-[#5C635A] leading-relaxed font-light">
              Expats in München kämpfen oft mit:
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-3">
              {problems.map((p, i) => (
                <li key={i} className="flex items-start gap-3 bg-white border border-[#E5E2DC] p-5">
                  <span className="mt-2 w-1.5 h-1.5 bg-[#C26D5C] shrink-0" />
                  <span className="text-[#1A201C] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 border-l-2 border-[#C26D5C] pl-6 py-2">
              <p className="font-serif text-xl text-[#1A201C] italic mb-2">Wir übernehmen die Einrichtung — und alles, was danach kommt.</p>
              <p className="text-[#5C635A] text-sm">Ziehen Sie ein — oder leben Sie weiter — in einem funktionalen, komfortablen, vollständig eingerichteten Zuhause.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section id="solution" className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-7">
            <p className="tag-line text-[#C26D5C] mb-4">White Glove Home Setup &amp; Concierge</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A201C] leading-[1.05]">
              Ihr Münchner Zuhause, vollständig eingerichtet und betreut —
              <span className="italic font-light text-[#C26D5C]"> ohne Chaos.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E2DC] p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#1C3326] flex items-center justify-center">
                <Rocket className="w-5 h-5 text-[#FAF9F6]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-[#1A201C]">Einzug &amp; Erste Einrichtung</h3>
            </div>
            <ul className="space-y-3 text-[#5C635A]">
              {moveInItems.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-[#E5E2DC] p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C26D5C] flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-[#FAF9F6]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-[#1A201C]">Nach dem Umzug — Laufende Unterstützung</h3>
            </div>
            <ul className="space-y-3 text-[#5C635A]">
              {beyondItems.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-[#1C3326] text-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-7">
              <p className="tag-line text-[#E8C9B6] mb-4">So funktioniert's</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                Möbel aufbauen lassen in fünf Schritte. Null Stress.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[#FAF9F6]/15">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#1C3326] p-7 md:p-8">
                <div className="font-serif text-4xl text-[#E8C9B6] mb-5">{s.n}</div>
                <h3 className="font-serif text-xl mb-3">{s.title}</h3>
                <p className="text-[#FAF9F6]/70 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28" data-testid="audience-section">
        <p className="tag-line text-[#C26D5C] mb-3 text-center">Für wen das gemacht ist</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight text-center mb-14 leading-tight">
          Der perfekte Möbelmontage-Service für Neuankömmlinge in München — und alle danach.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map(({ icon: Icon, label }) => (
            <div key={label} className="bg-white border border-[#E5E2DC] p-6 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
              <div className="w-10 h-10 bg-[#F5F3EF] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#1C3326]" strokeWidth={1.5} />
              </div>
              <p className="text-[#1A201C] text-sm leading-relaxed mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MORE THAN JUST MOVING */}
      <section id="services" className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="more-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
          <div className="md:col-span-5">
            <p className="tag-line text-[#C26D5C] mb-4">Mehr als nur ein Umzug</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
              Wir unterstützen Expats bei der Möbelmontage in jeder Phase ihres Lebens in München.
            </h2>
            <p className="mt-6 text-[#5C635A] leading-relaxed font-light">
              Typische Situationen, in denen wir helfen — von der ersten Nacht bis Jahre später.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-2.5">
              {moreSituations.map((s, i) => (
                <li key={i} className="bg-white border border-[#E5E2DC] p-5 flex items-start gap-3">
                  <span className="font-serif text-xs text-[#C26D5C] mt-0.5 tracking-wider">0{i + 1}</span>
                  <span className="text-[#1A201C] text-sm leading-relaxed flex-1">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-6">
            <p className="tag-line text-[#C26D5C] mb-4">Wählen Sie Ihr Münchner Wohnungs-Setup-Paket</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A201C] leading-[1.05]">
              Drei Wege für einen reibungslosen Einzug in München – inklusive Möbelmontage.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((s) => (
            <div key={s.code} className="bg-white border border-[#E5E2DC] flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`service-card-${s.code.toLowerCase()}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F3EF] relative">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 w-10 h-10 bg-[#1C3326] text-[#FAF9F6] flex items-center justify-center font-serif text-xl">{s.code}</div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <p className="tag-line text-[#C26D5C] mb-3">{s.tag}</p>
                <h3 className="font-serif text-2xl text-[#1A201C] mb-2">{s.title}</h3>
                <p className="text-[#5C635A] text-xs italic mb-4">{s.audience}</p>
                <p className="text-[#5C635A] text-sm leading-relaxed mb-5">{s.desc}</p>

                <ul className="space-y-2 mb-6 text-sm">
                  {s.includes.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                      <span className="text-[#5C635A]">{it}</span>
                    </li>
                  ))}
                </ul>

                {s.link && (
                  <Link to={s.link} className="inline-flex items-center gap-1 text-sm text-[#1C3326] underline underline-offset-4 decoration-[#C26D5C]/40 hover:decoration-[#C26D5C] mb-4" data-testid={`learn-more-${s.code.toLowerCase()}`}>
                    {s.learnMore}
                  </Link>
                )}

                <div className="mt-auto pt-5 border-t border-[#E5E2DC] flex items-center justify-between gap-3">
                  <p className="font-serif text-lg text-[#1A201C]">{s.price}</p>
                  <Button
                    size="sm"
                    onClick={() => openDialog({ source: `de-package-${s.code}`, service: `${s.code} — ${s.title}` })}
                    className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
                    data-testid={`schedule-call-${s.code.toLowerCase()}`}
                  >
                    Termin anfragen
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CORPORATE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center" data-testid="corporate-section">
        <div className="md:col-span-6">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={ABOUT_IMG} alt="Corporate Relocation München" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-6">
          <p className="tag-line text-[#C26D5C] mb-4">Corporate Relocation</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05] mb-6">
            Möbelmontage Partner für HR-Teams und internationale Mitarbeiter.
          </h2>
          <p className="text-[#5C635A] leading-relaxed font-light mb-6">
            Wir unterstützen nicht nur Einzüge, sondern den gesamten Mitarbeiter-Wohnungszyklus:
          </p>
          <ul className="space-y-2 text-[#1A201C] text-sm">
            {[
              "Mitarbeiter-Wohnungseinrichtung",
              "Zeitkritisches Onboarding",
              "Standardisierte Wohnungseinrichtung",
              "Laufender Support für Möbelmontage und Anpassungen",
            ].map((x) => (
              <li key={x} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 bg-[#C26D5C]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => openDialog({ source: "de-corporate", service: "Corporate Relocation" })}
            className="mt-8 rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
            data-testid="corporate-btn"
          >
            Sprechen Sie uns auf Corporate an
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-6">
              <p className="tag-line text-[#C26D5C] mb-4">Warum wir</p>
              <p className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
                Wir sind nicht nur Möbelmonteure.
                <span className="block italic font-light text-[#C26D5C]">Wir sind Ihr lokaler Partner in München.</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E2DC]">
            {whyUs.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-[#FAF9F6] p-7">
                <Icon className="w-5 h-5 text-[#1C3326] mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl text-[#1A201C] mb-2">{t}</p>
                <p className="text-[#5C635A] text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-24 text-center">
        <p className="tag-line text-[#C26D5C] mb-6">Pilotkunde</p>
        <p className="font-serif text-3xl sm:text-4xl text-[#1A201C] leading-snug italic font-light max-w-3xl mx-auto">
          „In München in eine vollständig eingerichtete Wohnung anzukommen, hat den Übergang mühelos gemacht."
        </p>
        <p className="mt-6 text-sm text-[#5C635A]">— Pilotkunde</p>
      </section>

      {/* MUNICH STRIP */}
      <section className="relative h-[40vh] min-h-[320px]">
        <img src={MUNICH_IMG} alt="München" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1C3326]/60" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 h-full flex items-center">
          <div className="max-w-2xl">
            <p className="tag-line text-[#E8C9B6] mb-4">Lokal. Verlässlich. Englisch & Deutsch.</p>
            <p className="font-serif text-4xl sm:text-5xl text-[#FAF9F6] leading-[1.05] tracking-tight">
              Wir kennen Schwabing, Maxvorstadt und den Aufzug, der in Au-Haidhausen nie funktioniert.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
        <p className="tag-line text-[#C26D5C] mb-4 text-center">Häufige Fragen</p>
        <p className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight text-center mb-14">
          Fragen, beantwortet.
        </p>
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-[#E5E2DC] bg-white px-6">
              <AccordionTrigger className="font-serif text-lg text-[#1A201C] hover:no-underline text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#5C635A] leading-relaxed font-light">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-24 text-center">
          <p className="font-serif text-4xl sm:text-6xl text-[#FAF9F6] tracking-tight leading-[1.05]">
            Sparen Sie sich das Erste-Woche-Chaos —
            <span className="block italic text-[#E8C9B6]">und alles danach.</span>
          </p>
          <p className="mt-6 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">
            Ziehen Sie selbstbewusst in München ein. Leben Sie hier mit Rückhalt.
          </p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: "de-final-cta", service: "Discovery Call" })}
            className="mt-10 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="footer-book-btn"
          >
            Kostenloses Erstgespräch buchen
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
